import React, { useMemo, useCallback, useRef } from "react";
import { Upload } from "antd";
import SbDrawer from "uiCore/SbDrawer/SbDrawer";
import SbButton from "uiCore/SbButton/SbButton";
import SbTour from "uiCore/SbTour/SbTour";
import TourStep from "uiCore/SbTour/TourStep";
import useReadyTour from "uiCore/SbTour/useReadyTour";
import {
  DrawerTitleStyle,
  FilesTable,
  ErrorMessage,
  UploadSectionTitle,
  UploadDescription,
  UploadRow,
  DraggerWrapper,
  UploadActions,
} from "./SbSbViewSourcesDrawerV2.styles";
import useEmployeeFiles from "./useEmployeeFiles";
import buildColumns from "./columns";
import DraggerContent from "./DraggerContent";
import FeedbackAlerts from "./FeedbackAlerts";
import {
  DRAWER_TITLE,
  DRAWER_WIDTH,
  DRAWER_PLACEMENT,
  DRAWER_ROOT_CLASS,
  FILES_SCROLL_THRESHOLD,
  FILES_SCROLL_Y,
  UPLOAD_SECTION_TITLE,
  ACCEPTED_FILE_FORMATS,
  UPLOAD_DESCRIPTION,
  SAVE_BUTTON_LABEL,
  SUBMIT_BUTTON_LABEL,
  SAVE_BUTTON_TITLE,
  SUBMIT_BUTTON_TITLE,
  SAVE_BUTTON_ID,
  SUBMIT_BUTTON_ID,
  DRAWER_TOUR_STEPS,
} from "constants/viewSourcesDrawer.constants";
import {
  BUTTON_TYPE_PRIMARY,
  BUTTON_TYPE_DEFAULT,
  TYPE_SECONDARY,
  TYPE_DANGER,
  SIZE_SMALL,
} from "constants/ui.constants";

// ─── Component ────────────────────────────────────────────────────────────────

const SbSbViewSourcesDrawerV2 = ({
  isDrawerOpen,
  toggleDrawer,
  accessToken,
  email,
  active = false,
  onTourComplete,
}) => {
  const {
    safetyScanning,
    fileName,
    safetyCheckPassedFile,
    alertState,
    uploadedFiles,
    error,
    loading,
    handleFileUpload,
    handleFileTabClick,
    handleDeleteClick,
    handleSaveClick,
    handleSubmitClick,
  } = useEmployeeFiles({ accessToken, email });

  const columns = useMemo(
    () => buildColumns(handleFileTabClick, handleDeleteClick, loading),
    [handleFileTabClick, handleDeleteClick, loading]
  );

  const handleBeforeUpload = useCallback((file) => {
    handleFileUpload(file);
    return false;
  }, [handleFileUpload]);

  // ── In-drawer tour ─────────────────────────────────────────────────────────
  // Runs when the main tour hands off (active). Rendered into the drawer via
  // getPopupContainer so it shares the drawer's stacking context; on finish/
  // dismiss it calls onTourComplete, which resumes the main page tour.
  const tourContainerRef = useRef(null);
  // Open the drawer tour only once it has slid in AND its files have loaded
  // (`ready: !loading`), so the spotlight measures the buttons at their settled
  // positions.
  const { open: drawerTourReady, remountKey: drawerTourRunId } = useReadyTour({
    active,
    ready: !loading,
  });

  const drawerTourSteps = DRAWER_TOUR_STEPS.map((step) => ({
    target: () => document.getElementById(step.targetId),
    placement: step.placement,
    ...(step.nextButtonText && { nextButtonProps: { children: step.nextButtonText } }),
    description: <TourStep title={step.title} description={step.description} />,
  }));

  return (
    <>
      <DrawerTitleStyle />
      <SbDrawer
        title={DRAWER_TITLE}
        rootClassName={DRAWER_ROOT_CLASS}
        placement={DRAWER_PLACEMENT}
        width={DRAWER_WIDTH}
        open={isDrawerOpen}
        onClose={toggleDrawer}
      >
        <div ref={tourContainerRef}>
        {/* ── Uploaded files table ── */}
        <FilesTable
          rowKey="file_id"
          columns={columns}
          dataSource={uploadedFiles}
          loading={loading}
          pagination={false}
          size={SIZE_SMALL}
          scroll={uploadedFiles.length > FILES_SCROLL_THRESHOLD ? { y: FILES_SCROLL_Y } : undefined}
        />

        {error && <ErrorMessage type={TYPE_DANGER}>{error}</ErrorMessage>}

        {/* ── Upload section ── */}
        <UploadSectionTitle level={5}>{UPLOAD_SECTION_TITLE}</UploadSectionTitle>
        <UploadDescription type={TYPE_SECONDARY}>{UPLOAD_DESCRIPTION}</UploadDescription>

        {/* ── Drop zone + action buttons ── */}
        <UploadRow>
          <DraggerWrapper>
            <Upload.Dragger
              accept={ACCEPTED_FILE_FORMATS}
              showUploadList={false}
              multiple={false}
              disabled={loading || safetyScanning}
              beforeUpload={handleBeforeUpload}
            >
              <DraggerContent safetyScanning={safetyScanning} />
            </Upload.Dragger>
          </DraggerWrapper>

          <UploadActions>
            <SbButton
              id={SAVE_BUTTON_ID}
              buttonType={BUTTON_TYPE_DEFAULT}
              title={SAVE_BUTTON_TITLE}
              onClick={handleSaveClick}
              loading={loading}
              disabled={loading}
            >
              {SAVE_BUTTON_LABEL}
            </SbButton>
            <SbButton
              id={SUBMIT_BUTTON_ID}
              buttonType={BUTTON_TYPE_PRIMARY}
              title={SUBMIT_BUTTON_TITLE}
              onClick={handleSubmitClick}
              loading={loading}
              disabled={loading}
            >
              {SUBMIT_BUTTON_LABEL}
            </SbButton>
          </UploadActions>
        </UploadRow>

        {/* ── Feedback alerts ── */}
        <FeedbackAlerts
          alertState={alertState}
          loading={loading}
          safetyScanning={safetyScanning}
          fileName={fileName}
          safetyCheckPassedFile={safetyCheckPassedFile}
        />

        {/* ── In-drawer tour (rendered inside the drawer via getPopupContainer) ── */}
        <SbTour
          key={drawerTourRunId}
          open={drawerTourReady}
          steps={drawerTourSteps}
          onClose={onTourComplete}
          onFinish={onTourComplete}
          getPopupContainer={() => tourContainerRef.current}
        />
        </div>
      </SbDrawer>
    </>
  );
};

export default SbSbViewSourcesDrawerV2;
